// src/components/ProtocolStats.jsx
import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { RefreshCw } from "lucide-react";

import { useGetStatsQuery } from "../../redux/protocol/protocolApi";
import Loader from "../../mainframes/Loader";

export default function ProtocolStats() {
  const theme = useTheme();
  const { data, isLoading, error, refetch } = useGetStatsQuery();
  // ✅ Add these states near top (inside ProtocolStats component)
const [statusPage, setStatusPage] = React.useState(0);
const [statusPageSize, setStatusPageSize] = React.useState(10);

const [unrevPage, setUnrevPage] = React.useState(0);
const [unrevPageSize, setUnrevPageSize] = React.useState(10);


  const stats = data?.data;
  const protocolStats = stats?.protocols || [];
  const unreviewedStats = stats?.unreviewedPackets || [];

  const totals = useMemo(() => {
    const totalProtocols = protocolStats.reduce((sum, s) => sum + (s.count || 0), 0);
    const activeProtocols = protocolStats.find((s) => s._id === "active")?.count || 0;
    const totalDevices = protocolStats.reduce((sum, s) => sum + (s.totalDevices || 0), 0);

    const avgConfidence =
      protocolStats.length > 0
        ? Math.round(
            protocolStats.reduce((sum, s) => sum + (s.avgConfidence || 0), 0) / protocolStats.length
          )
        : 0;

    return { totalProtocols, activeProtocols, totalDevices, avgConfidence };
  }, [protocolStats]);

  const statusRows = useMemo(
    () =>
      protocolStats.map((s, i) => ({
        id: `${s._id}_${i}`,
        status: s._id,
        count: s.count || 0,
        totalDevices: s.totalDevices || 0,
        avgConfidence: Math.round(s.avgConfidence || 0),
      })),
    [protocolStats]
  );

  const statusColumns = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 160,
        renderCell: (params) => {
          const v = params.value || "unknown";
          const map = {
            active: { bg: alpha(theme.palette.success.main, 0.12), fg: theme.palette.success.main },
            testing: { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.main },
            inactive: { bg: alpha(theme.palette.error.main, 0.1), fg: theme.palette.error.main },
          };
          const st = map[v] || { bg: alpha(theme.palette.text.primary, 0.08), fg: theme.palette.text.primary };
          return (
            <Chip
              size="small"
              label={v}
              sx={{ fontWeight: 900, borderRadius: 999, bgcolor: st.bg, color: st.fg }}
            />
          );
        },
      },
      { field: "count", headerName: "Count", width: 120 },
      { field: "totalDevices", headerName: "Total Devices", width: 150 },
      {
        field: "avgConfidence",
        headerName: "Avg Confidence",
        width: 160,
        renderCell: (params) => {
          const v = Number(params.value || 0);
          const color =
            v >= 80 ? theme.palette.success.main : v >= 50 ? theme.palette.warning.main : theme.palette.error.main;

          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
              <Box
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.text.primary, 0.08),
                  overflow: "hidden",
                }}
              >
                <Box sx={{ width: `${v}%`, height: "100%", bgcolor: color }} />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 900, width: 46, textAlign: "right" }}>
                {v}%
              </Typography>
            </Stack>
          );
        },
      },
    ],
    [theme]
  );

  const unreviewedRows = useMemo(
    () =>
      unreviewedStats.map((s, i) => ({
        id: `${s._id || "unknown"}_${i}`,
        header: s._id || "unknown",
        count: s.count || 0,
        totalRepeats: s.totalRepeats || 0,
        firstSeen: s.firstSeen ? new Date(s.firstSeen).toLocaleString() : "-",
        lastSeen: s.lastSeen ? new Date(s.lastSeen).toLocaleString() : "-",
      })),
    [unreviewedStats]
  );

  const unreviewedColumns = useMemo(
    () => [
      {
        field: "header",
        headerName: "Header",
        width: 180,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            sx={{
              fontWeight: 900,
              borderRadius: 1.6,
              bgcolor: alpha(theme.palette.info.main, 0.08),
            }}
          />
        ),
      },
      { field: "count", headerName: "Packet Count", width: 140 },
      { field: "totalRepeats", headerName: "Total Repeats", width: 140 },
      { field: "firstSeen", headerName: "First Seen", flex: 1, minWidth: 180 },
      { field: "lastSeen", headerName: "Last Seen", flex: 1, minWidth: 180 },
    ],
    [theme]
  );

  const StatCard = ({ label, value, hint }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        borderColor: alpha(theme.palette.divider, 0.9),
        bgcolor: alpha(theme.palette.background.paper, 0.9),
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          {hint && (
            <Tooltip title={hint}>
              <Chip size="small" label="i" variant="outlined" sx={{ height: 20, fontWeight: 900 }} />
            </Tooltip>
          )}
        </Stack>
        <Typography sx={{ fontWeight: 900, fontSize: 28, lineHeight: 1.1 }}>{value}</Typography>
      </Stack>
    </Paper>
  );

  if (isLoading) {
    return <Loader label="Loading statistics..." minHeight="280px" background="#fff" scale={0.72} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        {error?.data?.error || "Failed to load stats"}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Top bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            Protocol usage & unreviewed packet overview
          </Typography>
        </Box>

        <Button
          onClick={() => refetch()}
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Summary */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <StatCard label="Total Protocols" value={totals.totalProtocols} />
        <StatCard label="Active Protocols" value={totals.activeProtocols} />
        <StatCard label="Total Devices" value={totals.totalDevices} hint="Sum of devices mapped to each status bucket" />
        <StatCard label="Avg Confidence" value={`${totals.avgConfidence}%`} />
      </Box>

      {/* Status table */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 900 }}>Protocol Status Breakdown</Typography>
          <Chip size="small" label={`${statusRows.length} rows`} variant="outlined" sx={{ fontWeight: 900 }} />
        </Stack>
        <Divider />

        <Box sx={{ height: 420 }}>
          <DataGrid
            rows={statusRows || []}
            columns={statusColumns}
            disableRowSelectionOnClick
            pagination
            page={statusPage}
            onPageChange={(newPage) => setStatusPage(newPage)}
            pageSize={statusPageSize}
            onPageSizeChange={(newSize) => setStatusPageSize(newSize)}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                fontWeight: 900,
              },
              "& .MuiDataGrid-cell": { outline: "none" },
            }}
          />
        </Box>

      </Paper>

      {/* Unreviewed headers table */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 900 }}>Unreviewed Packets by Header</Typography>
          <Chip
            size="small"
            label={unreviewedRows.length === 0 ? "All clear 🎉" : `${unreviewedRows.length} headers`}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
        </Stack>
        <Divider />

        {unreviewedRows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="success">No unreviewed packets 🎉</Alert>
          </Box>
        ) : (
          <Box sx={{ height: 420 }}>
            <DataGrid
              rows={unreviewedRows}
              columns={unreviewedColumns}
              disableRowSelectionOnClick
              pagination
              page={unrevPage}
              onPageChange={(newPage) => setUnrevPage(newPage)}
              pageSize={unrevPageSize}
              onPageSizeChange={(newSize) => setUnrevPageSize(newSize)}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  fontWeight: 900,
                },
                "& .MuiDataGrid-cell": { outline: "none" },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
